import React, { useContext, useState } from 'react';
import Select, { Props, SelectInstance, StylesConfig } from 'react-select';
import { ThemeContext } from 'styled-components';
import constants, { fontSize, fontWeight } from '../../common/theme/constants';
import KitInput from './KitInput';
import { KitFormInfoText } from './KitFormControls';

// If you want to customize the icon
// const DropdownIndicator = (props: React.ComponentProps<typeof components.DropdownIndicator>) => {
//   return (
//     <components.DropdownIndicator {...props}>
//       <KitIcon icon='angle-down' size={`${constants.iconSize.s}rem`} />
//     </components.DropdownIndicator>
//   );
// };

export type OptionTypeBase = typeof Option & {
  label: string;
};

export const useKitSelectStyles = (error: boolean): StylesConfig<OptionTypeBase, boolean> => {
  const themeContext = useContext(ThemeContext);

  const getBottomBorderColor = (focused) => {
    if (error) {
      return themeContext.error;
    }
    if (focused) {
      return themeContext.focus;
    }
    return themeContext.select.bottomBorder;
  };

  return {
    input: (provided) => ({
      ...provided,
      color: themeContext.select.text,
      visibility: 'visible',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: themeContext.select.unselectedOptionText,
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: themeContext.select.icons,
      '&:hover': {
        color: themeContext.focus,
      },
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: themeContext.select.icons,
      '&:hover': {
        color: themeContext.focus,
      },
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none',
    }),
    loadingIndicator: (provided) => ({
      ...provided,
      color: themeContext.select.icons,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: themeContext.select.text,
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: themeContext.select.selectedOptionText,
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: themeContext.select.background,
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: themeContext.select.background,
    }),
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected
        ? themeContext.select.selectedOptionText
        : themeContext.select.unselectedOptionText,
      backgroundColor: state.isFocused ? themeContext.select.focusedOptionBackground : 'none',
      fontWeight: state.isSelected ? fontWeight.bold : fontWeight.normal,
      fontSize: `${fontSize.text_14}rem`,
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: themeContext.select.background,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: constants.zIndex.dropDown,
    }),
    container: (provided) => ({
      ...provided,
      border: 'none',
      boxShadow: 'none',
    }),
    control: (provided, state) => ({
      ...provided,
      border: 'none',
      borderRadius: 'none',
      boxShadow: 'none',
      minHeight: 'unset',
      '&:hover': {
        borderBottom: `${constants.border.width}px solid ${
          error ? themeContext.error : themeContext.focus
        }`,
      },
      borderBottom: `${constants.border.width}px solid ${getBottomBorderColor(state.isFocused)}`,
      backgroundColor:
        state.isFocused || state.isDisabled
          ? themeContext.select.focusedOptionBackground
          : themeContext.select.background,
      fontSize: `${fontSize.text_14}rem`,
    }),
  };
};

export interface KitSelectProps extends Props {
  isError?: boolean;
  className?: string;
  infoMessage?: string;
  inputId: string;
  onChange;
  renderHiddenInputForQA?: boolean;
}

const KitSelect = React.forwardRef<SelectInstance, Props>(
  (
    // renderHiddenInputForQA so that when QA does not need to do these tests, we don't have to render a hidden input
    {
      isError,
      infoMessage = null,
      inputId,
      onChange,
      renderHiddenInputForQA = false,
      ...otherProps
    }: KitSelectProps,
    ref
  ) => {
    const [selectedValue, setSelectedValue] = useState(
      (otherProps.value as OptionTypeBase)?.label ??
        (otherProps.defaultValue as OptionTypeBase)?.label
    );

    const handleChange = (e, action) => {
      // If there is an onChange handler then pass all details through to it
      // as usual otherwise do nothing but still set the value of the hidden input
      setSelectedValue(e.label);

      if (onChange) {
        onChange(e, action);
      }
    };

    const onKeyDown = (e) => {
      if (['Escape', 'Esc'].includes(e.code)) {
        e.stopPropagation();
      }
    };

    return (
      <>
        <Select
          // if you want to customize the icon
          // components={{ DropdownIndicator }}
          classNamePrefix='kit-select'
          onChange={handleChange}
          inputId={inputId}
          styles={useKitSelectStyles(isError)}
          aria-invalid={isError}
          menuPortalTarget={document.body}
          ref={ref}
          onKeyDown={onKeyDown}
          placeholder={otherProps.placeholder || null}
          {...otherProps}
        />
        {infoMessage && <KitFormInfoText isError={isError} text={infoMessage} aria-live='polite' />}
        {renderHiddenInputForQA && (
          <KitInput
            id={`${inputId ?? otherProps.id}-selected-value`}
            type='hidden'
            aria-hidden
            data-qa-id={otherProps['data-qa-id']}
            value={selectedValue || ''}
          />
        )}
      </>
    );
  }
);

export default KitSelect;
